/**
 * Experience Type Badge Component
 * Displays experience type badge with icon and label
 * Reusable across chat, landing page, and other screens
 * Follows Single Responsibility Principle - only handles badge display
 */

import React from 'react';
import Image from 'next/image';
import {
  typographyColors,
  spacing,
  borders,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import {
  getExperienceBadgeConfig,
  ExperienceType,
} from '@/components/chat/conversations/experiences/utils/experienceBadgeConfig';

export interface ExperienceTypeBadgeProps {
  /**
   * Type of experience (activities, package, riders-corner, signature, stays)
   */
  type?: ExperienceType;
  /**
   * Optional custom label (overrides default label for type)
   */
  label?: string;
  /**
   * Optional custom icon (overrides default icon for type)
   * Can be ReactNode (SVG) or string (image URL)
   */
  icon?: React.ReactNode | string;
  /**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Icon container background color (default: transparent)
   * Set to 'rgba(241, 241, 239, 0.19)' for landing page style
   */
  iconBackground?: string;
  /**
   * Icon container size (default: auto, can be '20px' for fixed size)
   */
  iconSize?: string;
  /**
   * Whether to use Image component for icon (when icon is a string URL)
   */
  useImageIcon?: boolean;
  /**
   * Size variant for the badge (default: 'default')
   * 'small' - for compact cards (180px width)
   * 'default' - for regular cards (286px width)
   */
  size?: 'small' | 'default';
}

/**
 * Experience Type Badge Component
 * Reusable badge component for displaying experience types
 * Follows Interface Segregation Principle - clean, focused interface
 */
export const ExperienceTypeBadge: React.FC<ExperienceTypeBadgeProps> = ({
  type,
  label,
  icon,
  className,
  iconBackground,
  iconSize,
  useImageIcon = false,
  size = 'default',
}) => {
  // Get default configuration for the type (if type is provided)
  const defaultConfig = type ? getExperienceBadgeConfig(type) : null;

  // Use custom props if provided, otherwise use defaults
  const badgeLabel = label || defaultConfig?.label || '';
  const badgeIcon = icon || defaultConfig?.icon || null;

  // Determine if icon is a string (URL) or ReactNode
  const isIconUrl = typeof badgeIcon === 'string';
  const shouldUseImage = useImageIcon && isIconUrl;

  // Size-specific styles
  const iconDimension = size === 'small' ? '14px' : '16px';
  const fontSize = size === 'small' ? '9px' : '10px';

  return (
    <div
      className={className}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: borders.radius.lg,
        paddingLeft: spacing[1],
        paddingRight: spacing[2],
        paddingTop: spacing[1],
        paddingBottom: spacing[1],
        display: 'flex',
        gap: spacing[1],
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {badgeIcon && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: iconSize ? '0' : spacing[0.5],
            borderRadius: borders.radius.md,
            backgroundColor: iconBackground || 'transparent',
            width: iconSize || 'auto',
            height: iconSize || 'auto',
            minWidth: iconSize || 'auto',
            minHeight: iconSize || 'auto',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {shouldUseImage && typeof badgeIcon === 'string' ? (
            <Image
              src={badgeIcon}
              alt={badgeLabel}
              width={parseInt(iconDimension)}
              height={parseInt(iconDimension)}
              className='object-contain object-center'
              style={{
                filter: 'brightness(0) invert(1)',
                width: iconDimension,
                height: iconDimension,
                maxWidth: iconDimension,
                maxHeight: iconDimension,
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              {typeof badgeIcon === 'string' ? (
                /* eslint-disable-next-line @next/next/no-img-element -- dynamic badge icon */
                <img
                  src={badgeIcon}
                  alt={badgeLabel}
                  style={{
                    width: iconDimension,
                    height: iconDimension,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                badgeIcon
              )}
            </div>
          )}
        </div>
      )}
      {badgeLabel && (
        <span
          style={{
            fontFamily: fontPrimary,
            fontSize: fontSize,
            fontWeight: typography.weight.bold,
            color: typographyColors.alternate,
            lineHeight: 'normal',
          }}
        >
          {badgeLabel}
        </span>
      )}
    </div>
  );
};
